import {
  CloseButton,
  createStyles,
  LoadingOverlay,
  Modal,
  ModalProps as MantineModalProps,
} from '@mantine/core';
import { isEqual, omit } from 'lodash/fp';
import React, { useEffect, useRef, useState } from 'react';

import { StoreListing, useStoreSettings } from '@portals/api/partners';
import {
  ModalProps,
  useConfirmationModal,
  WizardProvider,
} from '@portals/framework';

import { STEPS_MAP } from './steps-map';
import { OnCloseCallback, StepId, StoreListingWizardContext } from './types';
import { WizardContent } from './WizardContent';
import { mapStoreListingPricesToPaymentSettings } from '../../utils/store-listings.utils';

export interface StoreListingWizardModalProps
  extends ModalProps<{
    intent: 'create' | 'edit' | 'publish';
    storeListing: StoreListing | undefined;
  }> {}

export function StoreListingWizardModal({
  closeMe,
  data: { storeListing, intent },
}: StoreListingWizardModalProps) {
  const { classes } = useStyles();
  const asyncConfirmation = useConfirmationModal();

  const storeSettings = useStoreSettings();

  const [contextData, setContextData] = useState<StoreListingWizardContext>({
    intent,
    storeListing: storeListing,
    selectedProduct: storeListing?.product,
    paymentSettings: mapStoreListingPricesToPaymentSettings({
      currencies: storeSettings.data?.currencies,
      storeListingPrices: storeListing?.prices,
      defaultCurrency: storeSettings.data?.default_currency,
    }),
    paymentSettingsAccordionValues: [],
    paymentSettingsGeneralError: undefined,
  });

  const pristineContextData = useRef(contextData);

  useEffect(
    function initContextData() {
      if (!storeSettings.isFetched || !storeSettings.data) {
        return;
      }

      setContextData((prev) => {
        const updateContextData = {
          ...prev,
          paymentSettings: mapStoreListingPricesToPaymentSettings({
            currencies: storeSettings.data.currencies,
            storeListingPrices: storeListing?.prices,
            defaultCurrency: storeSettings.data.default_currency,
          }),
        };

        pristineContextData.current = updateContextData;
        return updateContextData;
      });
    },
    [storeListing?.prices, storeSettings.data, storeSettings.isFetched]
  );

  const hasChanges = () => {
    const keysToOmit = [
      'storeListing',
      'paymentSettingsAccordionValues',
      'intent',
      'paymentSettingsGeneralError',
    ];

    return !isEqual(
      omit(keysToOmit, pristineContextData.current),
      omit(keysToOmit, contextData)
    );
  };

  const onClose: OnCloseCallback = async ({ confirmBeforeClose }) => {
    if (!confirmBeforeClose || !hasChanges()) {
      closeMe();
      return;
    }

    const isConfirmed = await asyncConfirmation({
      title: 'Leave without saving?',
      description:
        'Closing this window will discard all changes you have made. Are you sure you want to leave?',
      confirmationLabel: 'Leave anyway',
      cancelLabel: 'Cancel',
    });

    if (isConfirmed) {
      closeMe();
    }
  };

  return (
    <Modal
      opened
      onClose={() => onClose({ confirmBeforeClose: true })}
      withCloseButton={false}
      size="clamp(1200px, 90%, 1700px)"
      padding={0}
      styles={modalStyles}
    >
      <LoadingOverlay visible={storeSettings.isFetching} />

      <CloseButton
        size="xl"
        onClick={() => onClose({ confirmBeforeClose: true })}
        className={classes.closeButton}
      />

      <WizardProvider<StoreListingWizardContext, StepId>
        steps={STEPS_MAP}
        initialStep={StepId.ChooseProduct}
        contextData={contextData}
        setContextData={setContextData}
      >
        <WizardContent onClose={onClose} />
      </WizardProvider>
    </Modal>
  );
}

const modalStyles: MantineModalProps['styles'] = (theme) => ({
  inner: { padding: 0 },
  content: {
    display: 'flex',
    minHeight: '90%',
  },
  header: { margin: 0 },
  body: { display: 'flex', flexGrow: 1 },
});

const useStyles = createStyles((theme) => ({
  closeButton: {
    position: 'absolute',
    top: theme.spacing.xl,
    right: theme.spacing.xl,
    zIndex: 1,
  },
}));
